import { isNullOrUndefined } from '@afterpay/utils'
import React, { ReactNode } from 'react'

import { ARCADE_CARDS_NAMES } from './arcade-cards/constants'
import { EagerArcadeCards, LazyArcadeCards } from './arcade-cards/lazy'
import * as arcadeIcons from './arcade-icons'
import { ARCADE_ICONS_NAMES } from './arcade-icons/iconNames'
import { ArcadeIconNames } from './ArcadeIcon'
import { ArcadePaymentIconNames } from './ArcadePaymentIcon'
import { ARCADE_ICON_VIEWBOX_MAPPING } from './arcadeViewBoxMapping'
import { AllIconNames, RegularIconNames } from './BaseIcon'
import * as icons from './bronte-icons'
import { BRONTE_ICONS_NAMES } from './bronte-icons/constants'
import { CARDS_NAMES as BRONTE_CARDS_NAMES } from './cards/constants'
import { EagerCards, LazyCards } from './cards/lazy'
import { TYPES } from './constants'
import { IconNames as BronteIconNames } from './Icon'
import { BRONTE_TO_ARCADE_ICONS_MAPPING, BRONTE_TO_ARCADE_PAYMENT_ICONS_MAPPING } from './iconMappings'
import { PaymentIconNames } from './PaymentIcon'

type AllPaymentIconNames = PaymentIconNames | ArcadePaymentIconNames

type IconType = (typeof TYPES)[keyof typeof TYPES]
const isArcadeIcon = (iconName: AllIconNames): iconName is ArcadeIconNames => Object.values<string>(ARCADE_ICONS_NAMES).includes(iconName)
const isArcadePaymentIcon = (iconName: AllIconNames): iconName is ArcadePaymentIconNames => Object.values<string>(ARCADE_CARDS_NAMES).includes(iconName)
const isBronteIcon = (iconName: AllIconNames): iconName is BronteIconNames => Object.values<string>(BRONTE_ICONS_NAMES).includes(iconName)
const isBrontePaymentIcon = (iconName: AllIconNames): iconName is PaymentIconNames => Object.values<string>(BRONTE_CARDS_NAMES).includes(iconName)
const isRegularIcon = (iconName: AllIconNames): iconName is RegularIconNames => isBronteIcon(iconName) || isArcadeIcon(iconName)
export const isPaymentIcon = (icon?: AllIconNames): icon is AllPaymentIconNames => {
  if (isNullOrUndefined(icon)) return false
  if (isBronteIcon(icon)) return false

  return isBrontePaymentIcon(icon) || isArcadePaymentIcon(icon)
}

export const getIconType = (isPayment: boolean, isBronteDesign: boolean): IconType =>
  isPayment ? (isBronteDesign ? TYPES.PAYMENT : TYPES.ARCADE_PAYMENT) : TYPES.DEFAULT

const findBronteCard = (name: string) => EagerCards[name] ?? LazyCards[name]
const findArcadeCard = (name: string) => EagerArcadeCards[name] ?? LazyArcadeCards[name]

const iconFinder = {
  paymentIcon: (name: AllPaymentIconNames, blackAndWhite: boolean, isBronteDesign: boolean) => {
    if (isBrontePaymentIcon(name)) {
      if (isBronteDesign) {
        const SvgIcon = findBronteCard(name)
        return SvgIcon ? <SvgIcon blackAndWhite={blackAndWhite} /> : null
      } else {
        const mappedName = BRONTE_TO_ARCADE_PAYMENT_ICONS_MAPPING[name]
        if (isNullOrUndefined(mappedName) || !isArcadePaymentIcon(mappedName)) {
          return null
        }
        const SvgIcon = findArcadeCard(mappedName)
        return SvgIcon ? <SvgIcon /> : null
      }
    } else {
      const SvgIcon = findArcadeCard(name)
      return SvgIcon ? <SvgIcon /> : null
    }
  },
  regularIcon: (name: RegularIconNames, isBronteDesign: boolean) => {
    if (isBronteIcon(name)) {
      if (isBronteDesign) {
        const SvgIcon = icons[name]
        return <SvgIcon />
      } else {
        const mappedName = BRONTE_TO_ARCADE_ICONS_MAPPING[name]
        if (isNullOrUndefined(mappedName) || !isArcadeIcon(mappedName)) {
          return null
        }
        const SvgIcon = arcadeIcons[mappedName]
        return <SvgIcon />
      }
    } else {
      const SvgIcon = arcadeIcons[name]
      return <SvgIcon />
    }
  },
}
interface LookupExtraParams {
  blackAndWhite: boolean
  isBronteDesign: boolean
}

export const iconLookup = (name: AllIconNames, params: LookupExtraParams): ReactNode => {
  if (isPaymentIcon(name)) {
    return iconFinder.paymentIcon(name, params.blackAndWhite, params.isBronteDesign)
  }
  if (isRegularIcon(name)) {
    return iconFinder.regularIcon(name, params.isBronteDesign)
  }
  return null
}

const findViewBoxLookupKey = (name: AllIconNames, isBronteDesign: boolean): ArcadeIconNames | ArcadePaymentIconNames | undefined => {
  if (isBronteDesign) {
    return
  }
  if (isArcadeIcon(name) || isArcadePaymentIcon(name)) {
    return name
  }
  if (isBronteIcon(name)) {
    return BRONTE_TO_ARCADE_ICONS_MAPPING[name]
  }
}

export const viewBoxLookup = (name: AllIconNames, isBronteDesign: boolean): string | undefined => {
  const key = findViewBoxLookupKey(name, isBronteDesign)
  return isNullOrUndefined(key) ? undefined : ARCADE_ICON_VIEWBOX_MAPPING[key]
}
